import type preact from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { api, ApiError, type Me } from './api.js'
import { Avatar } from './Avatar.js'
import { NAV_ITEMS, SECONDARY_ITEMS, SOS_ITEM, routeLesen } from './nav.js'
import { Anmelden } from './routes/Anmelden.js'
import { Einladung } from './routes/Einladung.js'
import { Start } from './routes/Start.js'
import { direktAktionen } from './start-aktionen.js'
import { lazy, Suspense } from 'preact/compat'
import { Entsperren } from './crypto/Entsperren.js'
import { e2ee, vorwaermen } from './crypto/lazy.js'
// Nur der Typ — zur Laufzeit wird der Speicher weiterhin lazy geladen, sonst
// zöge er die Krypto ins Startbundle (ARCHITECTURE.md §6).
import type * as Keystore from './crypto/keystore.js'
import { WiederherstellungsWarnung } from './crypto/Anker.js'
import { StandortBanner } from './routes/StandortBanner.js'
import { connect, disconnect, onReconnect } from './ws.js'
import { useOnline } from './useOnline.js'
import { quellenVergessen } from './media/quellen.js'
import { usePrefs } from './prefs.js'
import { t, text, useSprache } from './i18n/t.js'

/**
 * App-Shell.
 *
 * Drei Zustände: Einladung, Anmeldung, angemeldet. Alles andere kommt ab Phase 4
 * als lazy geladene Routen dazu — das Startbudget von 90 kB (ARCHITECTURE.md §6)
 * trägt nur die Shell, die Familienübersicht, den Status und SOS.
 */
export function App() {
  const online = useOnline()
  const perf = usePrefs((s) => s.perf)
  /*
   * Die Sprache steht hier, damit die ganze Hülle sie mitbekommt — und sie
   * dient unten als `key` für den offenen Bereich.
   *
   * `t()` liest die Sprache direkt aus dem Store, nicht über einen Hook. Das
   * ist Absicht: Sonst müssten sechshundert Aufrufstellen einen Hook
   * mitschleppen, und jede vergessene wäre ein Text, der beim Umschalten
   * deutsch stehen bliebe — bis jemand die Seite neu lädt. Ein `key`, der sich
   * ändert, baut den Bereich stattdessen einmal neu auf. Das kostet ein
   * Nachladen der offenen Seite und gilt dafür ausnahmslos.
   */
  const sprache = useSprache()
  const [route, setRoute] = useState(currentHash())
  const [me, setMe] = useState<Me | null | undefined>(undefined)
  /** undefined = noch nicht geprüft, false = gesperrt, true = entsperrt */
  const [entsperrt, setEntsperrt] = useState<boolean | undefined>(undefined)
  const [neuerSpeicher, setNeuerSpeicher] = useState(false)
  /** Ist der Passkey für den Speicher DIESES Geräts als Weg eingerichtet? */
  const [passkeyWeg, setPasskeyWeg] = useState(false)
  /** Vom Passkey geliefertes Geheimnis — entsperrt ohne Passwort. */
  const [prf, setPrf] = useState<ArrayBuffer | null>(null)

  useEffect(() => {
    const onHash = () => setRoute(currentHash())
    addEventListener('hashchange', onHash)
    return () => removeEventListener('hashchange', onHash)
  }, [])

  useEffect(() => {
    api.get<Me>('/me')
      .then(setMe)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.needsLogin) setMe(null)
        else setMe(null)
      })
  }, [])

  // Live-Verbindung nur, solange jemand angemeldet ist.
  useEffect(() => {
    if (!me) return
    connect()
    /*
     * Krypto im Hintergrund nachladen, damit der Chat nicht wartet — außer im
     * Sparsam-Modus.
     *
     * Der Krypto-Chunk sind 92 kB gzip, entpackt und geparst ein Vielfaches
     * davon. Wer im Datensparmodus unterwegs ist oder ein Gerät mit 2 GB RAM
     * hat, soll das nicht auf Verdacht bezahlen: Beim Öffnen des Chats wird es
     * ohnehin geladen, dann eben mit einer sichtbaren Wartezeit statt mit
     * einer unsichtbaren Rechnung (Anf. 66/67).
     */
    if (perf !== 'low') vorwaermen()
    return () => disconnect()
  }, [me, perf])

  /*
   * Delta-Sync nach einem Verbindungsabbruch (Anf. 30).
   *
   * Der WebSocket meldet nur, was passiert, solange er steht. Nach einem
   * Abbruch fehlt alles dazwischen. Statt beim Reconnect ALLES neu zu laden —
   * auf einer Mobilverbindung mehrere Sekunden für Daten, von denen sich fast
   * nichts geändert hat — holt der Client nur die Lücke.
   *
   * Der Cursor ist die SERVERZEIT aus der letzten Antwort. Die Uhr des
   * Telefons wäre gefährlich: Geht sie vor, würden Änderungen übersprungen.
   */
  useEffect(() => {
    if (!me) return
    return onReconnect(() => {
      const seit = localStorage.getItem('fh-sync-cursor')
      if (!seit) return

      void api.get<{ now: string; truncated: boolean }>(
        `/sync?since=${encodeURIComponent(seit)}`,
      ).then((delta) => {
        localStorage.setItem('fh-sync-cursor', delta.now)
        // Bei zu vielen Änderungen ist ein Delta kein Delta mehr. Dann wird
        // die Seite neu geladen, statt etwas Unvollständiges einzuspielen.
        if (delta.truncated) location.reload()
      }).catch(() => {
        // Klappt der Sync nicht, ist der Cursor unbrauchbar geworden: Beim
        // nächsten Versuch würde eine Lücke entstehen, die niemand bemerkt.
        localStorage.removeItem('fh-sync-cursor')
      })
    })
  }, [me])

  // Beim Anmelden einen Startpunkt setzen — vorher gibt es nichts nachzuholen.
  useEffect(() => {
    if (me) localStorage.setItem('fh-sync-cursor', new Date().toISOString())
  }, [me?.personId])

  // Ist der Schlüsselspeicher auf diesem Gerät schon eingerichtet — und ist der
  // Passkey als Weg hinein hinterlegt?
  useEffect(() => {
    if (!me) { setEntsperrt(undefined); return }
    void (async () => {
      const { istEingerichtet, wege } = await import('./crypto/keystore.js')
      setNeuerSpeicher(!(await istEingerichtet()))
      setPasskeyWeg((await wege()).includes('prf'))
      setEntsperrt(false)
    })()
  }, [me])

  // Hat der Passkey ein Geheimnis geliefert, entsperrt die App ohne Rückfrage.
  // Das ist der geplante Hauptweg (ADR-004): auflegen, drin sein. Klappt es
  // nicht, fällt die App auf die Passphrase zurück, statt hängen zu bleiben.
  useEffect(() => {
    if (!me || !prf || entsperrt !== false) return
    let abgebrochen = false
    void entsperrenMit('prf', prf, me.personId)
      .then(() => { if (!abgebrochen) setEntsperrt(true) })
      .catch(() => { if (!abgebrochen) setPrf(null) })
    return () => { abgebrochen = true }
  }, [me, prf, entsperrt])

  const einladungsToken = route.startsWith('/einladung/') ? route.slice('/einladung/'.length) : null

  if (einladungsToken) {
    return <Einladung token={einladungsToken} onDone={(m) => { setMe(m); location.hash = '#familie' }} />
  }
  if (me === undefined) return <Startbildschirm />
  if (me === null) {
    return (
      <Anmelden onDone={(m, prfGeheimnis) => {
        setPrf(prfGeheimnis ?? null)
        setMe(m)
        location.hash = '#familie'
      }} />
    )
  }

  // Der Schlüsselspeicher wird beim ersten Öffnen entsperrt. Ohne ihn liefe der
  // Chat blind — deshalb kommt der Schritt vor der Oberfläche, nicht danach.
  if (entsperrt === false) {
    // Wartet auf den Effekt oben: Er entsperrt still mit dem Passkey-Geheimnis.
    if (prf) return <Startbildschirm />

    return (
      <Entsperren
        neu={neuerSpeicher}
        passkeyWeg={passkeyWeg}
        mitSatz={async (satz) => { await entsperrenMit('passphrase', satz, me.personId) }}
        mitPasskey={async () => {
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await entsperrenMit('prf', geheimnis, me.personId)
          return true
        }}
        // Läuft auf einem bereits offenen Speicher: Der Speicherschlüssel wird
        // ein zweites Mal verschlüsselt, diesmal mit dem Geheimnis des Passkeys.
        // Die Inhalte bleiben unberührt, der Satz gilt weiter.
        passkeyNachruesten={async () => {
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const { wegHinzufuegen } = await import('./crypto/keystore.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await wegHinzufuegen('prf', geheimnis)
          return true
        }}
        onFertig={() => setEntsperrt(true)}
        // Der Notausgang. Ohne ihn ist der Entsperr-Bildschirm eine Sackgasse:
        // Er riegelt die App ab, bevor irgendein Menü erreichbar ist — wer den
        // Satz vergisst, kommt an den Abmelden-Knopf gar nicht mehr heran.
        // Aufgefallen beim ersten echten Gebrauch, nicht beim Lesen.
        onVergessen={async () => {
          await abmeldenUndLeeren()
          setEntsperrt(undefined)
          setMe(null)
        }}
      />
    )
  }
  if (entsperrt === undefined) return <Startbildschirm />

  const { bereich: roh, raum } = routeLesen(route)
  // Leerer Hash heißt „Startseite", nicht „unbekannter Bereich". Ohne diese
  // Zeile stand beim ersten Öffnen die Überschrift „Familie" doppelt da: einmal
  // als Reiter, einmal als Titel — und kein Reiter war hervorgehoben.
  const bereich = roh || NAV_ITEMS[0].id
  const unterbereich = SECONDARY_ITEMS.find((i) => i.id === bereich)
  const tab = NAV_ITEMS.find((i) => i.id === bereich)
  /*
   * SOS ist kein Reiter mehr, aber sehr wohl ein Bereich. Ohne diese Zeile
   * fiele der Alarmbildschirm auf die Familienseite zurück — der Knopf wäre
   * da und würde nichts öffnen.
   */
  const offenerBereich = tab?.id ?? unterbereich?.id ?? (bereich === SOS_ITEM.id ? SOS_ITEM.id : NAV_ITEMS[0].id)
  const aktiverTab = tab ?? (unterbereich ? NAV_ITEMS.find((i) => i.id === 'mehr')! : undefined)
  const titel = unterbereich?.label
    ?? (bereich === SOS_ITEM.id ? text('SOS') : undefined)
    ?? tab?.label
    ?? NAV_ITEMS[0].label

  return (
    <div class="fh-app">
      <header class="fh-bar">
        {/*
          Der Name der App steht oben und bleibt dort. Vertraut aussehen heißt
          nicht, sich als etwas anderes ausgeben.
        */}
        <h2 class="fh-bar__titel">{t('Family Hub')}</h2>
        <a class="fh-bar__aktion" href="#suche" title={t('Suchen')}>
          <span aria-hidden="true">🔍</span>
          <span class="fh-visually-hidden">{t('Suchen')}</span>
        </a>
        <a class="fh-bar__aktion" href="#mehr" title={t('Mehr')}>
          <span aria-hidden="true">⋮</span>
          <span class="fh-visually-hidden">{t('Mehr')}</span>
        </a>
      </header>

      <nav class="fh-tabs" aria-label={t('Hauptbereiche')}>
        {NAV_ITEMS.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            class="fh-tab"
            aria-current={item.id === aktiverTab?.id ? 'page' : undefined}
          >
            <span class="fh-tab__icon" aria-hidden="true">{item.icon}</span>
            <span>{t(item.label)}</span>
          </a>
        ))}
      </nav>

      <div class="fh-banner-stapel">
        {/*
          Steht VOR dem Offline-Banner: Eine laufende Wiederherstellung ist das
          Wichtigste, was die App jemand mitteilen kann (ADR-008, Risiko T13).
        */}
        <WiederherstellungsWarnung />

        {/*
          Der Standort-Banner steht HIER und nicht im Standort-Bereich. Wer ihn
          dorthin verschiebt, macht ihn unsichtbar, sobald jemand im Chat ist —
          und hebt damit ADR-009, Bedingung 3 auf.
        */}
        <StandortBanner />

        {!online && (
          <div class="fh-banner fh-banner--offline" role="status">
            {/* Symbol UND Text — Farbe trägt die Information nie allein (Anf. 58). */}
            <span aria-hidden="true">🟠</span>
            <span>{t('Keine Internetverbindung. Du siehst gespeicherte Inhalte.')}</span>
          </div>
        )}
      </div>

      <main class="fh-main" id="inhalt" tabIndex={-1}>
        {/*
          Die Überschrift bleibt im Dokument, wird aber unsichtbar, wenn der
          Reiter oben schon dasselbe Wort trägt: Zweimal „Chats" untereinander
          ist keine Orientierung, sondern Wiederholung. Für Bereiche ohne
          eigenen Reiter — Fotos, Dateien, Kalender — steht sie sichtbar da.
        */}
        <h1 class={tab ? 'fh-visually-hidden' : 'fh-h1'}>{t(titel)}</h1>
        <Bereich
          key={sprache}
          route={offenerBereich}
          raum={raum}
          me={me}
          onLoggedOut={() => setMe(null)}
        />
      </main>

      {/*
        SOS. Schwebt über jedem Bildschirm, in keinem Menü, nicht wegscrollbar
        (Anf. 28/61). Farbe trägt die Bedeutung nicht allein — das Wort steht
        im Knopf, und `aria-label` sagt, was passiert.
      */}
      <a
        class="fh-sos-knopf"
        href={`#${SOS_ITEM.id}`}
        aria-label={t('SOS — Notfall auslösen')}
        aria-current={bereich === SOS_ITEM.id ? 'page' : undefined}
      >
        <span aria-hidden="true">{SOS_ITEM.icon}</span>
        <span aria-hidden="true">{t(SOS_ITEM.label)}</span>
      </a>
    </div>
  )
}

/*
 * Nachgeladene Bereiche.
 *
 * Der Chat stand bis Phase 14 versehentlich im Startbundle: Er war statisch
 * importiert, also lag sein Code in `index-*.js`. Das Budget „Chat ≤ 60 kB"
 * fand deshalb keinen einzigen Chunk — und meldete genau deswegen „ok". Ein
 * Budget, das nichts findet, prüft nichts; seit Phase 14 lässt der leere
 * Treffer die Prüfung scheitern (scripts/check-bundle-budget.mjs).
 */
const Chat = lazy(() => import('./routes/Chat.js').then((m) => ({ default: m.Chat })))
const Mitglieder = lazy(() =>
  import('./routes/Mitglieder.js').then((m) => ({ default: m.Mitglieder })))
const Geraete = lazy(() => import('./routes/Geraete.js').then((m) => ({ default: m.Geraete })))
const Dateien = lazy(() => import('./routes/Dateien.js').then((m) => ({ default: m.Dateien })))
const Anruf = lazy(() => import('./routes/Anruf.js').then((m) => ({ default: m.Anruf })))
const Standort = lazy(() =>
  import('./routes/Standort.js').then((m) => ({ default: m.Standort })))
const Status = lazy(() => import('./routes/Status.js').then((m) => ({ default: m.Status })))
const Sos = lazy(() => import('./routes/Sos.js').then((m) => ({ default: m.Sos })))
const Kalender = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Kalender })))
const Aufgaben = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Aufgaben })))
const Notizen = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Notizen })))
const Fotos = lazy(() => import('./routes/Fotos.js').then((m) => ({ default: m.Fotos })))
const Suche = lazy(() => import('./routes/Suche.js').then((m) => ({ default: m.Suche })))
const Einstellungen = lazy(() =>
  import('./routes/Einstellungen.js').then((m) => ({ default: m.Einstellungen })))

function Bereich({ route, raum, me, onLoggedOut }: {
  route: string
  /** Aus `#chat/<roomId>`: der Raum, der direkt geöffnet werden soll. */
  raum: string | null
  me: Me
  onLoggedOut: () => void
}) {
  if (route === 'dateien') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Dateien />
      </Suspense>
    )
  }

  if (route === 'familie') {
    // Die Startseite ist NICHT lazy: Sie ist das Erste, was jemand sieht, und
    // ein Nachladen davor wäre genau die Wartezeit, die vermieden werden soll
    // (ARCHITECTURE.md §6).
    return <Start me={me} aktionen={direktAktionen()} />
  }

  // Die Module aus Phase 12. Alle lazy: Wer nie einen Termin anlegt, lädt
  // den Kalender nie (ARCHITECTURE.md §6).
  const module: Record<string, preact.JSX.Element> = {
    chat: <Chat me={me} oeffne={raum} />,
    mitglieder: <Mitglieder me={me} />,
    kalender: <Kalender me={me} />,
    aufgaben: <Aufgaben />,
    notizen: <Notizen />,
    fotos: <Fotos me={me} />,
    suche: <Suche />,
    einstellungen: <Einstellungen me={me} onGeloescht={onLoggedOut} />,
  }
  if (module[route]) {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        {module[route]}
      </Suspense>
    )
  }

  if (route === 'sos') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Sos me={me} />
      </Suspense>
    )
  }

  if (route === 'status') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Status me={me} />
      </Suspense>
    )
  }

  if (route === 'standort') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Standort me={me} />
      </Suspense>
    )
  }

  if (route === 'anrufen') {
    return (
      <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
        <Anruf me={me} />
      </Suspense>
    )
  }

  if (route === 'mehr') {
    return (
      <>
        {/*
          „Mehr" ist eine Liste von Zeilen, keine Wand aus Karten — dieselbe
          Zeile wie in der Familienübersicht und im Chat. Wer eine Einstellung
          sucht, soll nicht erst herausfinden, welche Sorte Kachel er vor sich
          hat.
        */}
        <ul class="fh-zeilen">
          <li>
            <div class="fh-zeile">
              <Avatar name={me.displayName} />
              <span class="fh-zeile__mitte">
                <span class="fh-zeile__name">{me.displayName}</span>
                <span class="fh-zeile__unter">{t(rollenName(me.role))}</span>
              </span>
            </div>
          </li>
          {SECONDARY_ITEMS.map((item) => (
            <li key={item.id}>
              <a class="fh-zeile" href={`#${item.id}`}>
                <span class="fh-avatar" aria-hidden="true">{item.icon}</span>
                <span class="fh-zeile__mitte">
                  <span class="fh-zeile__name">{t(item.label)}</span>
                </span>
                <span class="fh-zeile__rechts" aria-hidden="true">›</span>
              </a>
            </li>
          ))}
        </ul>

        <h2 class="fh-h2">{t('Meine Geräte')}</h2>
        <Suspense fallback={<p class="fh-muted">{t('Wird geladen…')}</p>}>
          <Geraete onLoggedOut={onLoggedOut} />
        </Suspense>

        <h2 class="fh-h2">{t('Sicherheit')}</h2>
        <SicherheitsBereich me={me} />

        <div class="fh-card">
          <button
            class="fh-btn fh-btn--gefahr"
            onClick={() => {
              // Beim Abmelden wird der Schlüsselspeicher geleert, nicht nur der
              // Schlüssel vergessen (SECURITY.md §3.5).
              void abmeldenUndLeeren().then(onLoggedOut)
            }}
          >
            {t('Abmelden')}
          </button>
        </div>
      </>
    )
  }

  return (
    <div class="fh-card">
      <h2 class="fh-h2">{t('Noch nicht gebaut')}</h2>
      <p class="fh-muted">
        {t('Diesen Bereich gibt es noch nicht. Das Gerüst steht, die Funktion noch nicht.')}
      </p>
    </div>
  )
}

/**
 * Abmelden und den Schlüsselspeicher LEEREN — nicht nur den Schlüssel vergessen
 * (SECURITY.md §3.5).
 *
 * Steht bewusst an einer Stelle für beide Aufrufer: den Abmelden-Knopf und den
 * Notausgang „Satz vergessen". Zwei Kopien dieser Reihenfolge würden
 * auseinanderdriften, und die Stelle, an der etwas übrig bliebe, wäre die
 * unangenehmste denkbare.
 */
async function abmeldenUndLeeren(): Promise<void> {
  await api.post('/auth/logout').catch(() => {
    // Auch ohne erreichbaren Server muss das Gerät sauber werden. Die Sitzung
    // läuft dann serverseitig ab, statt sofort zu enden — das lokale
    // Schlüsselmaterial ist trotzdem weg.
  })
  // Signierte Bild-URLs hängen an der Sitzung. Nach einem Kontowechsel auf
  // demselben Gerät wären sie wertlos — der Zwischenspeicher wüsste es nur nicht.
  quellenVergessen()
  const keystore = await import('./crypto/keystore.js')
  await keystore.loeschen()
  // Der Service Worker hält seit Phase 13 einen Daten-Cache. Er gehört zum
  // selben Aufräumen wie der Schlüsselspeicher: Das Abmelden ist der Moment,
  // in dem ein Gerät die Hand wechseln könnte.
  navigator.serviceWorker?.controller?.postMessage({ type: 'fh-abmelden' })
}

/**
 * Entsperrt den Schlüsselspeicher und startet die Krypto.
 *
 * Beide Wege enden hier: Passkey-Geheimnis (PRF) oder Passphrase. Der
 * Unterschied liegt nur im ersten Argument — alles danach ist gleich.
 */
async function entsperrenMit(
  methode: 'prf' | 'passphrase',
  geheimnis: ArrayBuffer | string,
  personId: string,
): Promise<void> {
  const speicher = await import('./crypto/keystore.js')
  await speicher.entsperren(methode, geheimnis)

  const krypto = await e2ee()
  const geraete = await api.get<Array<{ id: string; isCurrent: boolean }>>('/devices')
  const eigenes = geraete.find((d) => d.isCurrent) ?? geraete[0]
  if (eigenes) await krypto.starten(personId, eigenes.id)

  await recoverySchluesselSicherstellen(speicher)
}

/**
 * Legt einmalig das Recovery-Schlüsselpaar dieses Geräts an.
 *
 * Ohne den öffentlichen Teil kann niemand einen Anker-Anteil FÜR uns
 * verschlüsseln — die Familie sähe uns in der Auswahl gar nicht erst. Der
 * private Teil bleibt im versiegelten Speicher; der Server bekommt ihn nie.
 *
 * Scheitert der Netzaufruf, ist das kein Grund, die Anmeldung abzubrechen:
 * beim nächsten Entsperren versucht es die App erneut.
 */
async function recoverySchluesselSicherstellen(
  speicher: typeof Keystore,
): Promise<void> {
  try {
    if (await speicher.holen('recovery-keypair')) return
    const { verwahrerSchluesselpaar } = await import('@fh/crypto')
    const paar = await verwahrerSchluesselpaar()
    const privat = new Uint8Array(await crypto.subtle.exportKey('pkcs8', paar.privateKey))
    await api.put('/recovery/public-key', { publicKey: paar.publicKey })
    await speicher.ablegen('recovery-keypair', { publicKey: paar.publicKey, privateKey: privat })
  } catch {
    // Nächster Versuch beim nächsten Entsperren.
  }
}

/**
 * Der Sicherheitsbereich lädt Anker und Sicherheitsnummer nach — beide
 * brauchen die Krypto-Bibliothek und haben im Startbundle nichts verloren.
 */
function SicherheitsBereich({ me }: { me: Me }) {
  const [Inhalt, setInhalt] = useState<preact.ComponentType<{ me: Me }> | null>(null)

  useEffect(() => {
    void import('./crypto/SicherheitsBereich.js').then((m) => setInhalt(() => m.SicherheitsBereich))
  }, [])

  if (!Inhalt) return <p class="fh-muted">{t('Wird geladen…')}</p>
  return <Inhalt me={me} />
}

/** Kein leerer Bildschirm, während `/me` läuft (Anf. 71). */
function Startbildschirm() {
  return (
    <div class="fh-anmelden">
      <p class="fh-muted">{t('Wird geladen…')}</p>
    </div>
  )
}

function rollenName(role: string): string {
  const namen: Record<string, string> = {
    admin: text('Administrator'), parent: text('Elternteil'), adult: text('Erwachsener'),
    child: text('Kind'), guest: text('Gast'),
  }
  return namen[role] ?? role
}

function currentHash(): string {
  return location.hash.replace(/^#/, '')
}
